/**
 * The "New customer" form's fields, the same on every way in (v1 completion
 * C5, docs/features/v1-completion/mockups/C_create_customer.html): the
 * `customers/new` page, the catalogue's dialog, a won lead and Promo Office.
 * Each caller chooses which sections it shows; the state and its rules live
 * in shared-core's `domain/new-customer`.
 *
 * Field errors come from the server and sit under their inputs
 * (.claude/rules/api-validation.md). As the name is typed, "Already in
 * CIS?" lists customers with a similar name from the caller's own customer
 * search; nothing blocks the create.
 */
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { Loader2, Search } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  AddressCountry,
  CisCustomer,
  CustomerOwnerOption,
  FoundAddress,
  NewCustomerForm,
} from "@shared-core/data/api-types";
import { extractFieldErrors } from "@shared-core/data/validation";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import {
  DEFAULT_COUNTRY_CODE,
  HOUSE_ACCOUNT,
  fillNewCustomerFromPremise,
  type NewCustomerDraft,
  type NewCustomerField,
} from "@shared-core/domain/new-customer";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { paths } from "@shared-core/routing/routes";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";

/** "Already in CIS?" waits for this many typed characters. */
const MIN_MATCH_LENGTH = 3;
const MATCH_LIMIT = 5;

const SELECT_CLASS =
  "border-input bg-input-background flex h-9 w-full rounded-md border px-3 py-1 text-sm outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:border-destructive disabled:opacity-50";

export type NewCustomerSection =
  "customer" | "address" | "owners" | "orderContact";

export function NewCustomerFields({
  idPrefix,
  form,
  draft,
  onDraftChange,
  errors,
  disabled = false,
  showMarket = true,
  showAddress = true,
  showMatches = true,
  showOrderContact = false,
  sectionFrame,
  ownerNote,
}: {
  idPrefix: string;
  /** The form's choices; null while they load (owners then offer House Account only). */
  form: NewCustomerForm | null;
  draft: NewCustomerDraft;
  onDraftChange: (draft: NewCustomerDraft) => void;
  errors: Partial<Record<NewCustomerField, string>>;
  disabled?: boolean;
  showMarket?: boolean;
  showAddress?: boolean;
  showMatches?: boolean;
  /** The optional "Order contact" step (v1 completion D2): the page and a won lead show it. */
  showOrderContact?: boolean;
  /** Wraps each section: the page puts each in a card with its heading; a dialog leaves them bare. */
  sectionFrame?: (section: NewCustomerSection, content: ReactNode) => ReactNode;
  /** Who the sales rep defaults to on this way in, e.g. "(lead owner)". */
  ownerNote?: { userId: string; label: string };
}) {
  const frame =
    sectionFrame ??
    ((_section: NewCustomerSection, content: ReactNode) => content);
  const set = (patch: Partial<NewCustomerDraft>) =>
    onDraftChange({ ...draft, ...patch });
  const fieldId = (field: string) => `${idPrefix}-${field}`;
  const describedBy = (field: NewCustomerField, hintId?: string) =>
    [errors[field] ? fieldId(`${field}-error`) : null, hintId ?? null]
      .filter(Boolean)
      .join(" ") || undefined;
  const errorFor = (field: NewCustomerField) =>
    errors[field] ? (
      <p
        id={fieldId(`${field}-error`)}
        className="text-sm text-red-600 dark:text-red-400"
      >
        {errors[field]}
      </p>
    ) : null;

  const nameSection = (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="space-y-1 sm:col-span-2">
        <label htmlFor={fieldId("name")} className="text-sm font-medium">
          Name
        </label>
        <Input
          id={fieldId("name")}
          value={draft.name}
          onChange={(e) => set({ name: e.target.value })}
          disabled={disabled}
          aria-invalid={errors.name ? true : undefined}
          aria-describedby={describedBy("name")}
        />
        {errorFor("name")}
        {showMatches && <AlreadyInCis name={draft.name} idPrefix={idPrefix} />}
      </div>
      {showMarket && (
        <div className="space-y-1">
          <label htmlFor={fieldId("market")} className="text-sm font-medium">
            Market{" "}
            <span className="font-normal text-muted-foreground">
              (optional)
            </span>
          </label>
          <Input
            id={fieldId("market")}
            list={fieldId("markets")}
            value={draft.market}
            onChange={(e) => set({ market: e.target.value })}
            disabled={disabled}
            aria-invalid={errors.market ? true : undefined}
            aria-describedby={describedBy("market", fieldId("market-hint"))}
          />
          <datalist id={fieldId("markets")}>
            {(form?.markets ?? []).map((market) => (
              <option key={market} value={market} />
            ))}
          </datalist>
          {errorFor("market")}
          <p
            id={fieldId("market-hint")}
            className="text-xs text-muted-foreground"
          >
            Used to group customers in reports.
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {frame("customer", nameSection)}
      {showAddress &&
        frame(
          "address",
          <AccountAddressFields
            idPrefix={idPrefix}
            draft={draft}
            onDraftChange={onDraftChange}
            errors={errors}
            disabled={disabled}
            errorFor={errorFor}
            describedBy={describedBy}
          />,
        )}
      {frame(
        "owners",
        <div className="space-y-2">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <OwnerSelect
              id={fieldId("sales-rep")}
              label="Sales rep"
              value={draft.salesRepUserId}
              options={form?.sales_rep_options ?? []}
              you={form?.caller_user_id ?? null}
              note={ownerNote}
              onChange={(salesRepUserId) => set({ salesRepUserId })}
              disabled={disabled}
              error={errorFor("salesRep")}
              describedBy={describedBy("salesRep", fieldId("sales-rep-hint"))}
              hint={
                <p
                  id={fieldId("sales-rep-hint")}
                  className="text-xs text-muted-foreground"
                >
                  Defaults to you if you are a sales rep.
                </p>
              }
            />
            <OwnerSelect
              id={fieldId("csr")}
              label="CSR"
              value={draft.csrUserId}
              options={form?.csr_options ?? []}
              you={form?.caller_user_id ?? null}
              onChange={(csrUserId) => set({ csrUserId })}
              disabled={disabled}
              error={errorFor("csr")}
              describedBy={describedBy("csr", fieldId("csr-hint"))}
              hint={
                <p
                  id={fieldId("csr-hint")}
                  className="text-xs text-muted-foreground"
                >
                  Defaults to you if you are a CSR, otherwise House Account.
                </p>
              }
            />
          </div>
          <p className="text-xs text-muted-foreground">
            They see this customer in their portfolios. Managers and admins see
            every customer.
          </p>
        </div>,
      )}
      {showOrderContact &&
        frame(
          "orderContact",
          <div className="space-y-2">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <label
                  htmlFor={fieldId("order-contact-name")}
                  className="text-sm font-medium"
                >
                  Contact name
                </label>
                <Input
                  id={fieldId("order-contact-name")}
                  value={draft.orderContactName}
                  onChange={(e) => set({ orderContactName: e.target.value })}
                  disabled={disabled}
                  aria-invalid={errors.orderContactName ? true : undefined}
                  aria-describedby={describedBy("orderContactName")}
                />
                {errorFor("orderContactName")}
              </div>
              <div className="space-y-1">
                <label
                  htmlFor={fieldId("order-contact-email")}
                  className="text-sm font-medium"
                >
                  Contact email
                </label>
                <Input
                  id={fieldId("order-contact-email")}
                  type="email"
                  value={draft.orderContactEmail}
                  onChange={(e) => set({ orderContactEmail: e.target.value })}
                  disabled={disabled}
                  aria-invalid={errors.orderContactEmail ? true : undefined}
                  aria-describedby={describedBy(
                    "orderContactEmail",
                    fieldId("order-contact-hint"),
                  )}
                />
                {errorFor("orderContactEmail")}
              </div>
            </div>
            <p
              id={fieldId("order-contact-hint")}
              className="text-xs text-muted-foreground"
            >
              Optional. New quotes and orders for this customer start with this
              person as the contact. No email is sent to them.
            </p>
          </div>,
        )}
    </div>
  );
}

/** Said before the create, rather than the customer vanishing from the caller's lists after it. */
export function NotInPortfolioNotice() {
  return (
    <p
      role="status"
      className="rounded-md border border-amber-400 px-3 py-2 text-sm text-amber-800 dark:border-amber-700 dark:text-amber-300"
    >
      Not in your portfolio: you won&apos;t see this customer once it is
      created. Make yourself the sales rep or CSR, or carry on.
    </p>
  );
}

function OwnerSelect({
  id,
  label,
  value,
  options,
  you,
  note,
  onChange,
  disabled,
  error,
  describedBy,
  hint,
}: {
  id: string;
  label: string;
  value: string;
  options: readonly CustomerOwnerOption[];
  you: string | null;
  note?: { userId: string; label: string };
  onChange: (value: string) => void;
  disabled: boolean;
  error: ReactNode;
  describedBy: string | undefined;
  hint: ReactNode;
}) {
  const optionLabel = (option: CustomerOwnerOption) => {
    if (note?.userId === option.user_id) return `${option.name} ${note.label}`;
    if (option.user_id === you) return `${option.name} (you)`;
    return option.name;
  };
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={SELECT_CLASS}
      >
        <option value={HOUSE_ACCOUNT}>House Account</option>
        {options.map((option) => (
          <option key={option.user_id} value={option.user_id}>
            {optionLabel(option)}
          </option>
        ))}
      </select>
      {error}
      {hint}
    </div>
  );
}

/** Where *Find by postcode* has got to. */
type PostcodeLookup =
  | { kind: "idle" }
  | { kind: "searching" }
  | { kind: "found"; premises: FoundAddress[] }
  | { kind: "refused"; message: string };

function AccountAddressFields({
  idPrefix,
  draft,
  onDraftChange,
  errors,
  disabled,
  errorFor,
  describedBy,
}: {
  idPrefix: string;
  draft: NewCustomerDraft;
  onDraftChange: (draft: NewCustomerDraft) => void;
  errors: Partial<Record<NewCustomerField, string>>;
  disabled: boolean;
  errorFor: (field: NewCustomerField) => ReactNode;
  describedBy: (field: NewCustomerField, hintId?: string) => string | undefined;
}) {
  const adapter = useDataAdapter();
  const [countries, setCountries] = useState<AddressCountry[] | null>(null);
  const [lookup, setLookup] = useState<PostcodeLookup>({ kind: "idle" });
  const set = (patch: Partial<NewCustomerDraft>) =>
    onDraftChange({ ...draft, ...patch });
  const fieldId = (field: string) => `${idPrefix}-${field}`;

  useEffect(() => {
    let cancelled = false;
    adapter
      .listAddressCountries()
      .then((list) => {
        if (!cancelled) setCountries(list);
      })
      .catch(() => {
        // The default stays chosen, so the form never sends an empty country.
        if (!cancelled) setCountries([]);
      });
    return () => {
      cancelled = true;
    };
  }, [adapter]);

  const findAddress = async () => {
    const postcode = draft.postcode.trim();
    if (!postcode) return;
    setLookup({ kind: "searching" });
    try {
      const premises = await adapter.findAddressesForNewCustomer(postcode);
      if (premises.length === 1)
        onDraftChange(fillNewCustomerFromPremise(draft, premises[0]));
      setLookup(
        premises.length === 0
          ? { kind: "refused", message: "No addresses found: type it in." }
          : { kind: "found", premises },
      );
    } catch (err) {
      const fieldError = extractFieldErrors(err)[0];
      setLookup({
        kind: "refused",
        message:
          fieldError?.message ??
          mutationErrorMessage(
            err,
            "Postcode lookup didn't answer: type the address in.",
          ),
      });
    }
  };

  const textInput = (
    field: "addressLine1" | "addressLine2" | "town",
    errorField: NewCustomerField | null,
    text: ReactNode,
  ) => (
    <div className="space-y-1">
      <label htmlFor={fieldId(field)} className="text-sm font-medium">
        {text}
      </label>
      <Input
        id={fieldId(field)}
        value={draft[field]}
        onChange={(e) => set({ [field]: e.target.value })}
        disabled={disabled}
        aria-invalid={errorField && errors[errorField] ? true : undefined}
        aria-describedby={errorField ? describedBy(errorField) : undefined}
      />
      {errorField && errorFor(errorField)}
    </div>
  );

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <label htmlFor={fieldId("postcode")} className="text-sm font-medium">
            Postcode
          </label>
          <div className="flex gap-2">
            <Input
              id={fieldId("postcode")}
              value={draft.postcode}
              onChange={(e) => set({ postcode: e.target.value })}
              onKeyDown={(e) => {
                // Enter finds the address rather than submitting the form.
                if (e.key !== "Enter") return;
                e.preventDefault();
                void findAddress();
              }}
              disabled={disabled}
              aria-invalid={errors.postcode ? true : undefined}
              aria-describedby={describedBy(
                "postcode",
                lookup.kind === "refused"
                  ? fieldId("lookup-refusal")
                  : undefined,
              )}
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => void findAddress()}
              disabled={
                disabled ||
                lookup.kind === "searching" ||
                draft.postcode.trim() === ""
              }
            >
              {lookup.kind === "searching" ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Search className="size-3.5" />
              )}
              Find
            </Button>
          </div>
          {errorFor("postcode")}
          {lookup.kind === "refused" && (
            <p
              id={fieldId("lookup-refusal")}
              role="status"
              className="text-xs text-muted-foreground"
            >
              {lookup.message}
            </p>
          )}
          {lookup.kind === "found" && lookup.premises.length > 1 && (
            <select
              aria-label="Choose the address"
              defaultValue=""
              onChange={(e) => {
                const premise = lookup.premises[Number(e.target.value)];
                if (premise)
                  onDraftChange(fillNewCustomerFromPremise(draft, premise));
              }}
              disabled={disabled}
              className={SELECT_CLASS}
            >
              <option value="" disabled>
                {`${lookup.premises.length} addresses`}
              </option>
              {lookup.premises.map((premise, index) => (
                <option key={index} value={index}>
                  {premise.label}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="space-y-1">
          <label htmlFor={fieldId("country")} className="text-sm font-medium">
            Country
          </label>
          <select
            id={fieldId("country")}
            value={draft.countryCode}
            onChange={(e) => set({ countryCode: e.target.value })}
            disabled={disabled}
            aria-invalid={errors.country ? true : undefined}
            aria-describedby={describedBy("country")}
            className={SELECT_CLASS}
          >
            {countries === null || countries.length === 0 ? (
              <option value={DEFAULT_COUNTRY_CODE}>United Kingdom</option>
            ) : (
              countries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))
            )}
          </select>
          {errorFor("country")}
        </div>
        <div className="sm:col-span-2">
          {textInput("addressLine1", "addressLines", "Address line 1")}
        </div>
        {textInput(
          "addressLine2",
          null,
          <>
            Address line 2{" "}
            <span className="font-normal text-muted-foreground">
              (optional)
            </span>
          </>,
        )}
        {textInput("town", "town", "Town")}
      </div>
      <p className="text-xs text-muted-foreground">
        Saved as the customer&apos;s invoice and delivery address.
      </p>
    </div>
  );
}

type Matches =
  | { kind: "idle" }
  | { kind: "loaded"; customers: CisCustomer[]; total: number };

/** "Already in CIS?": customers with a similar name, from the caller's own customer search. */
function AlreadyInCis({ name, idPrefix }: { name: string; idPrefix: string }) {
  const adapter = useDataAdapter();
  const typed = useDebouncedValue(name.trim(), 300);
  const [matches, setMatches] = useState<Matches>({ kind: "idle" });

  useEffect(() => {
    if (typed.length < MIN_MATCH_LENGTH) {
      setMatches({ kind: "idle" });
      return;
    }
    let cancelled = false;
    adapter
      .listCisCustomers({ q: typed, pageSize: MATCH_LIMIT })
      .then((page) => {
        if (!cancelled)
          setMatches({
            kind: "loaded",
            customers: page.items,
            total: page.total,
          });
      })
      .catch(() => {
        // A failed check is not a reason to stop someone creating: say nothing.
        if (!cancelled) setMatches({ kind: "idle" });
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, typed]);

  if (matches.kind !== "loaded" || matches.customers.length === 0) return null;
  return (
    <div
      role="status"
      aria-labelledby={`${idPrefix}-matches-heading`}
      className="mt-2 rounded-md border border-dashed border-border bg-surface px-3 py-2 text-sm"
    >
      <p>
        <strong id={`${idPrefix}-matches-heading`}>Already in One?</strong>{" "}
        <span className="text-xs text-muted-foreground">
          {matches.total === 1
            ? "1 customer has a similar name."
            : `${matches.total} customers have a similar name.`}
        </span>
      </p>
      <ul className="mt-1 divide-y divide-border">
        {matches.customers.map((customer) => (
          <li
            key={customer.id}
            className="flex items-center justify-between gap-2 py-1"
          >
            <span>
              {customer.name}{" "}
              <span className="font-mono text-xs text-muted-foreground">
                {customer.tharstern_customer_code
                  ? `${customer.customer_code} · ${customer.tharstern_customer_code}`
                  : customer.customer_code}
              </span>
              {!customer.is_active && (
                <span className="ml-2 rounded-full border border-border px-2 text-[11px] text-muted-foreground">
                  Closed
                </span>
              )}
            </span>
            <Link
              to={paths.customerDetail({
                customerId:
                  customer.tharstern_customer_code ?? customer.customer_code,
              })}
              className="text-primary underline"
            >
              Open
            </Link>
          </li>
        ))}
      </ul>
      <p className="mt-1 text-xs text-muted-foreground">
        Not one of these? Carry on.
      </p>
    </div>
  );
}
